import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { ArrowLeft, User, LogOut, History, Trash2, Calendar, Eye, Bookmark, Briefcase, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import AuthModal from '../components/AuthModal';
import { useSavedRoles } from '../hooks/useSavedRoles';

const SavedRoadmapsPage = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [savedRoadmaps, setSavedRoadmaps] = useState([]);
  const { savedRoles, removeRole } = useSavedRoles(user);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setShowAuthModal(true);
      setLoading(false);
      return;
    }
    fetchSavedRoadmaps();
  }, [user]);

  const fetchSavedRoadmaps = async () => {
    try {
      const { data, error } = await supabase
        .from('career_roadmaps')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setSavedRoadmaps(data || []);
    } catch (error) {
      console.error('Error fetching roadmaps:', error);
      toast.error('Failed to load saved roadmaps');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRoadmap = async (id) => {
    if (!window.confirm('Are you sure you want to delete this roadmap?')) return;
    try {
      const { error } = await supabase
        .from('career_roadmaps')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);
      if (error) throw error;
      setSavedRoadmaps(prev => prev.filter(rm => rm.id !== id));
      toast.success('Roadmap deleted');
    } catch (error) {
      console.error('Error deleting roadmap:', error);
      toast.error('Failed to delete roadmap');
    }
  };

  const handleRemoveRole = (roleTitle) => {
    removeRole(roleTitle);
    toast.success('Career removed from bookmarks');
  };

  const handleViewRoadmap = (roadmap) => {
    navigate(`/roadmap/${roadmap.id}`);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  if (!user && !showAuthModal) return null;

  return (
    <div className="min-h-screen" style={{ position: 'relative', fontFamily: "'Inter', sans-serif" }}>

      {/* Background — same as RoadmapPage */}
      <div style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundImage: 'url(/bluewhite.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        zIndex: -1,
        transform: 'translateZ(0)',
        willChange: 'transform'
      }} />

      {/* Header — same glass style as RoadmapPage */}
      <header className="border-b fixed top-0 left-0 right-0 z-50 backdrop-blur-xl" style={{
        borderColor: 'rgba(255, 255, 255, 0.3)',
        background: 'rgba(255, 252, 248, 0.65)',
        boxShadow: '0 4px 30px rgba(0, 0, 0, 0.08)',
        WebkitBackdropFilter: 'blur(20px)',
        backdropFilter: 'blur(20px)'
      }}>
        <div className="container mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/roadmap')}
                style={{ color: '#2d1b0e' }}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center gap-2">
                <img
                  src="https://customer-assets.emergentagent.com/job_path-explorer-12/artifacts/cyj4b8vo_Fig%20Icon%202.png"
                  alt="Fig Logo"
                  className="w-8 h-8 object-contain"
                />
                <h1 className="text-lg sm:text-xl hidden sm:block" style={{
                  fontFamily: "'Chango', cursive",
                  fontWeight: 400,
                  color: '#2d1b0e',
                  letterSpacing: '0.04em'
                }}>
                  Fig Careers
                </h1>
              </div>
            </div>

            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2" style={{
                    borderColor: 'rgba(45, 27, 14, 0.2)',
                    color: '#2d1b0e',
                    background: 'rgba(255, 250, 245, 0.6)'
                  }}>
                    <User className="w-4 h-4" />
                    <span className="hidden sm:inline">{user.email}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleSignOut}>
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 pt-28 pb-12 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto space-y-12">

          {loading && (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-400 border-t-transparent" />
              <p className="mt-4 text-slate-500">Loading your saved items...</p>
            </div>
          )}

          {!loading && (
            <>
              {/* ── Saved Roadmaps ── */}
              <section>
                <div className="flex items-center gap-3 mb-6">
                  <History className="w-7 h-7 text-blue-500" />
                  <h2 className="text-2xl font-bold" style={{ color: '#2d1b0e' }}>Saved Roadmaps</h2>
                  <span className="text-sm text-slate-500 font-normal">({savedRoadmaps.length})</span>
                </div>

                {savedRoadmaps.length === 0 ? (
                  <Card className="border-2 border-dashed" style={{ background: 'rgba(255,252,248,0.7)', borderColor: 'rgba(45,27,14,0.15)' }}>
                    <CardContent className="flex flex-col items-center justify-center py-10">
                      <History className="w-12 h-12 text-slate-300 mb-3" />
                      <p className="text-slate-500 mb-4">No saved roadmaps yet</p>
                      <Button onClick={() => navigate('/roadmap')} style={{ background: '#2d1b0e', color: '#fff' }}>
                        Create a Roadmap
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-4">
                    {savedRoadmaps.map((roadmap) => (
                      <Card key={roadmap.id} style={{ background: 'rgba(255,252,248,0.85)', borderColor: 'rgba(45,27,14,0.12)' }} className="hover:shadow-md transition-shadow">
                        <CardHeader>
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <CardTitle className="text-lg mb-1" style={{ color: '#2d1b0e' }}>
                                {roadmap.title || 'Career Roadmap'}
                              </CardTitle>
                              <CardDescription className="space-y-2">
                                <div className="flex items-center gap-2 text-sm text-slate-500">
                                  <Calendar className="w-4 h-4" />
                                  {new Date(roadmap.created_at).toLocaleDateString('en-US', {
                                    year: 'numeric', month: 'long', day: 'numeric'
                                  })}
                                </div>
                                <div className="flex flex-wrap gap-1.5 mt-1">
                                  {roadmap.form_data?.interests?.split(',').slice(0, 3).map((interest, idx) => (
                                    <Badge key={idx} variant="outline" className="text-xs">
                                      {interest.trim()}
                                    </Badge>
                                  ))}
                                </div>
                              </CardDescription>
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => handleViewRoadmap(roadmap)} className="gap-2">
                                <Eye className="w-4 h-4" />
                                <span className="hidden sm:inline">View</span>
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeleteRoadmap(roadmap.id)}
                                className="text-red-500 hover:bg-red-50 hover:text-red-600"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="font-medium text-slate-500 mb-1">Skills</p>
                              <p className="text-slate-700 line-clamp-2">{roadmap.form_data?.skills || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="font-medium text-slate-500 mb-1">Role Models</p>
                              <p className="text-slate-700 line-clamp-2">{roadmap.form_data?.roleModels || 'N/A'}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </section>

              {/* ── Bookmarked Careers ── */}
              <section>
                <div className="flex items-center gap-3 mb-6">
                  <Bookmark className="w-7 h-7 text-blue-500" />
                  <h2 className="text-2xl font-bold" style={{ color: '#2d1b0e' }}>Bookmarked Careers</h2>
                  <span className="text-sm text-slate-500 font-normal">({savedRoles.length})</span>
                </div>

                {savedRoles.length === 0 ? (
                  <Card className="border-2 border-dashed" style={{ background: 'rgba(255,252,248,0.7)', borderColor: 'rgba(45,27,14,0.15)' }}>
                    <CardContent className="flex flex-col items-center justify-center py-10">
                      <Bookmark className="w-12 h-12 text-slate-300 mb-3" />
                      <p className="text-slate-500 mb-4">No bookmarked careers yet</p>
                      <Button onClick={() => navigate('/discover')} style={{ background: '#2d1b0e', color: '#fff' }}>
                        Discover Careers
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-4 sm:grid-cols-2">
                    {savedRoles.map((role) => (
                      <Card key={role.id || role.title} style={{ background: 'rgba(255,252,248,0.85)', borderColor: 'rgba(45,27,14,0.12)' }} className="hover:shadow-md transition-shadow">
                        <CardHeader className="pb-2">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <Briefcase className="w-4 h-4 text-blue-500 shrink-0" />
                                <CardTitle className="text-base truncate" style={{ color: '#2d1b0e' }}>
                                  {role.title}
                                </CardTitle>
                              </div>
                              {role.match_percentage && (
                                <span className="inline-block text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                                  {role.match_percentage}% match
                                </span>
                              )}
                            </div>
                            <button
                              onClick={() => handleRemoveRole(role.title)}
                              className="text-slate-400 hover:text-red-400 transition-colors shrink-0 mt-0.5"
                              title="Remove bookmark"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-0 space-y-3">
                          {role.description && (
                            <p className="text-sm text-slate-600 line-clamp-2">{role.description}</p>
                          )}
                          {(role.salary || role.salary_range) && (
                            <div className="flex items-center gap-1.5 text-sm text-slate-500">
                              <DollarSign className="w-3.5 h-3.5" />
                              {role.salary
                                ? role.salary
                                : typeof role.salary_range === 'object'
                                  ? `${role.salary_range.min ? Math.round(role.salary_range.min / 1000) + 'k' : ''} – ${role.salary_range.max ? Math.round(role.salary_range.max / 1000) + 'k' : ''}`.trim()
                                  : role.salary_range}
                            </div>
                          )}
                          {role.companies && role.companies.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                              {role.companies.slice(0, 3).map((c, i) => (
                                <Badge key={i} variant="outline" className="text-xs">{typeof c === 'string' ? c : JSON.stringify(c)}</Badge>
                              ))}
                            </div>
                          )}
                          {role.why_it_fits && (
                            <div className="flex flex-wrap gap-1.5">
                              {(Array.isArray(role.why_it_fits) ? role.why_it_fits.slice(0, 3) : role.why_it_fits.split('•').filter(Boolean).slice(0, 3)).map((tag, i) => (
                                <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-600">{typeof tag === 'string' ? tag.trim() : tag}</span>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </main>

      <AuthModal open={showAuthModal} onOpenChange={setShowAuthModal} />
    </div>
  );
};

export default SavedRoadmapsPage;
